import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { Divider, Form, Header, Icon } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { PurchasOptionsType } from '~/api/services/climaProgram'
import InfoPopup from '~/components/InfoPopup'
import { formatDateGMT, formatNumber } from '~/utils/helpers'
import dayjs from 'dayjs'
import ErrorIcon from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ErrorIcon'

interface Props {
  options: PurchasOptionsType
}

export const LACTOSE_DRY_GROUP_KEY = 3
export const AGOLINE_PER_DAY_ERROR_KEY = 'agolineContentPerDay'
export const MINERAL_TOTAL_ERROR_KEY = 'mineralTotal'

const AnimalGroupsFields: React.FC<Props> = ({ options }) => {
  const { t } = useTranslation()
  const { control, formState: { errors }, setError, clearErrors } = useFormContext()

  const watchAnimalGroup = useWatch({ control, name: 'animalGroup' })
  const watchCustomerMix = useWatch({ control, name: 'customerMix' })
  const watchAgolineContent = useWatch({ control, name: 'agolineContent' })
  const watchFeedSupplierId = useWatch({ control, name: 'feedSupplierId' })
  const watchFeedProductId = useWatch({ control, name: 'feedProductId' })
  const watchAnimalGroups = useWatch({ control, name: 'animalGroups' })
  const watchFeedingFrom = useWatch({ control, name: 'feedingFrom' })
  const watchFeedingToo = useWatch({ control, name: 'feedingTo' })
  const watchPurchasedAmount = useWatch({ control, name: 'purchasedFeedAmount' })

  const feedSupplier = options?.feedSuppliers?.find(feedSupplier => feedSupplier?.id === watchFeedSupplierId)
  const feedProduct = feedSupplier?.products?.find(product => product.id === watchFeedProductId)

  const groupLabel = useMemo(() => {
    if (!options?.animalGroups) return ''
    const group = options.animalGroups.find(g => g.key === watchAnimalGroup)
    return group?.label || ''
  }, [watchAnimalGroup, options?.animalGroups])

  const getAgolineContentPerDay = useMemo(() => {
    const agolineContentPct = watchCustomerMix === 1 ? watchAgolineContent : parseFloat(feedProduct?.agolineContent || '0') || 0
    return (index: number) => {
      const dailyMineralIntakePerCow = watchAnimalGroups?.[index]?.dailyMineralIntakePerCow || 0
      const value = agolineContentPct * dailyMineralIntakePerCow / 100
      return Number.isNaN(value) ? 0 : value
    }
  }, [watchCustomerMix, watchAgolineContent, feedProduct?.agolineContent, watchAnimalGroups])

  useEffect(() => {
    const id = 'animal-groups-error-pulse-style'
    if (!document.getElementById(id)) {
      const style = document.createElement('style')
      style.id = id
      style.textContent = `
        @keyframes errorPulse {
          0%   { box-shadow: 0 0 0 0 rgba(159, 58, 56, 0.6); background-color: rgba(159, 58, 56, 0.08); }
          70%  { box-shadow: 0 0 0 10px rgba(159, 58, 56, 0); background-color: transparent; }
          100% { box-shadow: 0 0 0 0 rgba(159, 58, 56, 0); background-color: transparent; }
        }
      `
      document.head.appendChild(style)
    }
    return () => { document.getElementById(id)?.remove() }
  }, [])

  useEffect(() => {
    const groupsToValidate: number[] = (watchAnimalGroups || []).map((_, i) => i)

    const agolineSourceFilled = watchCustomerMix === 1
      ? watchAgolineContent !== undefined && watchAgolineContent !== ''
      : feedProduct?.agolineContent !== undefined && feedProduct?.agolineContent !== ''

    groupsToValidate.forEach((index) => {
      const dailyMineralIntakePerCow = watchAnimalGroups?.[index]?.dailyMineralIntakePerCow
      const errorKey = `${AGOLINE_PER_DAY_ERROR_KEY}_${index}`

      if (!agolineSourceFilled || dailyMineralIntakePerCow === undefined || dailyMineralIntakePerCow === '') {
        clearErrors(errorKey)
        return
      }

      const agolineContentPerDay = getAgolineContentPerDay(index)
      if (agolineContentPerDay < 1) {
        setError(errorKey, { type: 'min', message: t('Agolin per cow per day must not be less than 1g') })
      } else {
        clearErrors(errorKey)
      }
    })
  }, [getAgolineContentPerDay, watchAnimalGroups])

  function transformValue(value) {
    if (value) {
      return parseFloat(value?.toString()?.replace(',', '.'))
    }

    return 0
  }

  const mineralTotalCalculated = useMemo(() => {
    const startFeedingDate = dayjs(formatDateGMT(watchFeedingFrom))
    const endFeedingDate = dayjs(formatDateGMT(watchFeedingToo))
    const days = endFeedingDate.diff(startFeedingDate, 'days') + 1

    const totalFeedConsumed = watchAnimalGroups.reduce((previouseValue, currentValue) => {
      const cowNumber = transformValue(currentValue?.avgCowsNumberFedWithAgoline || 0)
      const mineralAvg = transformValue(currentValue?.dailyMineralIntakePerCow)
      return previouseValue + (cowNumber * mineralAvg)
    }, 0)

    return totalFeedConsumed / 1000 * days
  }, [
    watchAnimalGroup,
    watchCustomerMix,
    watchAgolineContent,
    watchAnimalGroups,
    watchFeedingFrom,
    watchFeedingToo
  ])

  useEffect(() => {
    const amountTransformed = parseFloat(watchPurchasedAmount?.toString()?.replace(/[^0-9,.']/g, '').replace(',', '.'))
    if(mineralTotalCalculated > amountTransformed) {
      setError(MINERAL_TOTAL_ERROR_KEY, { type: 'max', message: t('The amount fed ({{amountTransformed}} kg) exceeds the amount of feed purchased ({{mineralTotalCalculated}} kg).',
        { amountTransformed: formatNumber(mineralTotalCalculated, 2), mineralTotalCalculated: formatNumber(amountTransformed, 2) })
      })
    } else {
      clearErrors(MINERAL_TOTAL_ERROR_KEY)
    }
  }, [mineralTotalCalculated, watchPurchasedAmount])

  function renderAnimalGroupFields(label, key, index) {
    const agolineContentPerDay = getAgolineContentPerDay(index)
    const agolineErrorKey = `${AGOLINE_PER_DAY_ERROR_KEY}_${index}`
    const hasAgolineError = errors[agolineErrorKey]

    return <div style={{ marginTop: '1.5em', marginBottom: '1em' }} key={key}>
      <Header as='h4'>{label}</Header>
      <Divider />

      <Form.Group widths='equal'>
        <FormFields.Input
          name={`animalGroups[${index}].avgCowsNumberFedWithAgoline`}
          control={control}
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {key === 1
                ? t('Number of lactating cows fed with Agolin (average)')
                : t('Number of dry cows fed with Agolin (average)')}
              <ErrorIcon control={control} name={`animalGroups[${index}].avgCowsNumberFedWithAgoline`} />
            </div>
          }
          typeInput='controled'
          withoutAsterisk
          allowDecimals={false}
          noErrorMessage
          rules={{
            required: true,
            min: { value: 1, message: t('Value must be at least 1') },
            max: { value: 100000, message: t('Value must be at most 100000') }
          }}
        />

        <FormFields.Input
          name={`animalGroups[${index}].dailyMineralIntakePerCow`}
          withoutAsterisk
          control={control}
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {key === 1
                ? t('Mineral amount per lactating cow per day')
                : t('Mineral amount per dry cow per day')}
              <ErrorIcon control={control} name={`animalGroups[${index}].dailyMineralIntakePerCow`} />
            </div>
          }
          typeInput='controled'
          decimalScale={2}
          allowDecimals
          noErrorMessage
          suffix=' g'
          rules={{
            required: true,
            min: { value: 1, message: t('The agolin content must not be less than 1.00 g per cow per day.') },
            max: { value: 1000000, message: t('Value must be at most 1000000') }
          }}
        />
        <Form.Field
          key={hasAgolineError ? 1 : 0}
          error={!!hasAgolineError}
          style={hasAgolineError ? { animation: 'errorPulse 2s ease', borderRadius: 4 } : undefined}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '1em' }}>
            <Icon name='chart bar' />
            {key === 1
              ? t('This corresponds to g Agolin per lactating cow per day')
              : t('This corresponds to g Agolin per dry cow per day')}
            <InfoPopup content={t('Must not be less than 1g')} styleIcon={{ fontSize: '1.2em' }} />
            <ErrorIcon control={control} name={agolineErrorKey} />
          </label>
          <div style={{ paddingBottom: '1em' }}>{`${formatNumber(agolineContentPerDay, 2)} g`}</div>
        </Form.Field>
      </Form.Group>
      <Divider />
    </div>
  }

  if (watchAnimalGroup === LACTOSE_DRY_GROUP_KEY) {
    const mineralTotalError = errors[MINERAL_TOTAL_ERROR_KEY]

    return <>
      {options?.animalGroups.map((animalGroup, index) => {
        return animalGroup.key !== LACTOSE_DRY_GROUP_KEY && renderAnimalGroupFields(animalGroup.label, animalGroup.key, index - 1)
      })}
      <Form.Field
        key={mineralTotalError ? 1 : 0}
        error={!!mineralTotalError}
        style={{ display: 'flex', flexDirection: 'column', fontWeight: 'bold', ...(mineralTotalError ? { animation: 'errorPulse 2.0s ease', borderRadius: 4 } : {}) }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '1em' }}>
          <Icon name='chart bar' />
          {t('Amount of minerals fed [calculated] (in kg)')}
          <ErrorIcon control={control} name={MINERAL_TOTAL_ERROR_KEY} />
        </label>
        <div style={{ paddingBottom: '1em' }}>{`${formatNumber(mineralTotalCalculated || 0, 2)} kg`}</div>
      </Form.Field>
    </>
  }

  const mineralTotalError = errors[MINERAL_TOTAL_ERROR_KEY]

  return (
    <>
      {renderAnimalGroupFields(groupLabel, watchAnimalGroup, 0)}

      <Form.Field
        key={mineralTotalError ? 1 : 0}
        error={!!mineralTotalError}
        style={{ display: 'flex', flexDirection: 'column', fontWeight: 'bold', ...(mineralTotalError ? { animation: 'errorPulse 2.0s ease', borderRadius: 4 } : {}) }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '1em' }}>
          <Icon name='chart bar' />
          {t('Amount of minerals fed [calculated] (in kg)')}
          <ErrorIcon control={control} name={MINERAL_TOTAL_ERROR_KEY} />
        </label>
        <div style={{ paddingBottom: mineralTotalError ? '0.25em' : '1em' }}>{`${formatNumber(mineralTotalCalculated || 0, 2)} kg`}</div>
      </Form.Field>
    </>
  )
}

export default AnimalGroupsFields
